import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useToast } from '@/hooks/useToast';
import { useQuotesNeeded } from '@/hooks/queries';
import { getCurrencySymbol } from '@shared/constants/billing.js';

export default function AdminQuotesNeededPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();
  
  const page = parseInt(searchParams.get('page') || '1', 10);
  
  const { data, isLoading: loading, refetch } = useQuotesNeeded(page, 50);

  const apps = data?.apps || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (newPage) => {
    navigate(`/admin-portal/quotes-needed?page=${newPage}`);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading apps needing quotes...</p>
          </div>
        </div>
      </AdminPortalLayout>
    );
  }

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              💰 Quotes Needed
            </h1>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
          <p className="text-gray-600">
            {pagination.total === 0 
              ? 'No apps need quotes' 
              : `${pagination.total} app${pagination.total !== 1 ? 's' : ''} waiting for quotes`}
          </p>
        </div>

        {/* Apps List */}
        {apps.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              No apps need quotes. All quote requests have been handled!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {apps.map((app) => (
              <div
                key={app.appId}
                className="bg-white rounded-lg border-2 border-blue-200 p-6 shadow-sm"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">
                      {app.appName || 'Untitled App'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      User: {app.userEmail}
                    </p>
                    {/* User's Stated Budget */}
                    {(app.userBudgetBuild || app.userBudgetMonthly) && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs font-semibold text-amber-800 mb-1">💰 User's Budget</p>
                        <div className="flex gap-4 text-sm">
                          {app.userBudgetBuild && (
                            <span className="text-amber-700">
                              Build: <strong className="text-amber-900">{getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.userBudgetBuild).toFixed(0)}</strong>
                            </span>
                          )}
                          {app.userBudgetMonthly && (
                            <span className="text-amber-700">
                              Monthly: <strong className="text-amber-900">{getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.userBudgetMonthly).toFixed(0)}/mo</strong>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {app.appDescription && (
                      <p className="text-sm text-gray-600 mb-3">
                        {app.appDescription}
                      </p>
                    )}
                    {app.createdAt && (
                      <p className="text-xs text-gray-500">
                        Created: {formatDate(app.createdAt)}
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        <strong>Next Step:</strong> Click "Create Quote" to review tasks and send a quote to the user. Once the quote is sent and approved, work can begin.
                      </p>
                    </div>
                  </div>
                  <div className="ml-4 flex gap-2">
                    <button
                      onClick={() => navigate(`/admin-portal/app/${app.appId}/create-quote`)}
                      className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium text-sm whitespace-nowrap"
                    >
                      Create Quote →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} apps
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasPrevPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                          pagination.page === pageNum
                            ? 'bg-zapt-purple text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasNextPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminPortalLayout>
  );
}
